import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ChatInterfaceRedesigned from "@/components/ChatInterfaceRedesigned";
import WelcomeModal from "@/components/WelcomeModal";
import InstallPrompt from "@/components/InstallPrompt";
import UpdateNotification from "@/components/UpdateNotification";
import WhatsNewModal from "@/components/WhatsNewModal";
import ConversationSidebar from "@/components/ConversationSidebar";
import ImageGenerator from "@/components/ImageGenerator";
import ReviewPrompt from "@/components/ReviewPrompt";
import NexiBottomNav from "@/components/NexiBottomNav";
import { Plus, Wand2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const Home = () => {
  const [currentConversation, setCurrentConversation] = useState(null);
  const [userName, setUserName] = useState("");
  const [showWelcome, setShowWelcome] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showImageGenerator, setShowImageGenerator] = useState(false);
  const [activeTab, setActiveTab] = useState("home");
  const { isRTL } = useLanguage();

  useEffect(() => {
    const storedName = localStorage.getItem("nexi_user_name");
    if (storedName) {
      setUserName(storedName);
    } else {
      setShowWelcome(true);
    }
  }, []);

  const handleNameSubmit = (name) => {
    setUserName(name);
    localStorage.setItem("nexi_user_name", name);
    setShowWelcome(false);
  };

  const handleSelectConversation = useCallback((conversationId, mode) => {
    setCurrentConversation(conversationId);
    setSidebarOpen(false);
    setActiveTab("chats");
  }, []);

  const handleNewConversation = useCallback(() => {
    setCurrentConversation(null);
    setSidebarOpen(false);
    setActiveTab("home");
  }, []);

  const handleGoHome = useCallback(() => {
    setCurrentConversation(null);
    setSidebarOpen(false);
    setActiveTab("home");
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "home") {
      setCurrentConversation(null);
    } else if (tab === "chats") {
      setSidebarOpen(true);
    }
  };

  return (
    <div className="nexi-app-container" dir={isRTL ? 'rtl' : 'ltr'}>
      {showWelcome && <WelcomeModal onNameSubmit={handleNameSubmit} />}
      <WhatsNewModal />
      <UpdateNotification />
      <InstallPrompt />

      <ConversationSidebar
        isOpen={sidebarOpen}
        onToggle={toggleSidebar}
        currentConversationId={currentConversation}
        onSelectConversation={handleSelectConversation}
        onNewConversation={handleNewConversation}
        currentMode="chat"
      />
      
      {/* Fixed Header */}
      <header className="nexi-header">
        <div className="nexi-header-inner">
          {/* Logo */}
          <motion.button
            onClick={handleGoHome}
            className="nexi-logo-btn"
            whileTap={{ scale: 0.98 }}
          >
            <div className="nexi-logo-icon">
              <span>N</span>
            </div>
            <span className="nexi-logo-text">
              <span>NEXI</span>
            </span>
          </motion.button>
          
          {/* Actions */}
          <div className="nexi-header-actions">
            <motion.button
              onClick={() => setShowImageGenerator(true)}
              className="nexi-header-btn"
              whileTap={{ scale: 0.95 }}
              data-testid="image-generator-btn"
            >
              <Wand2 size={20} />
            </motion.button>
            <motion.button
              onClick={handleGoHome}
              className="nexi-header-btn primary"
              whileTap={{ scale: 0.95 }}
              data-testid="new-chat-btn"
            >
              <Plus size={20} />
            </motion.button>
          </div>
        </div>
      </header>

      {/* Main Content - Chat Interface */}
      <main className={`nexi-main ${sidebarOpen ? "sidebar-open" : ""}`}>
        <ChatInterfaceRedesigned
          mode="chat"
          conversationId={currentConversation}
          onConversationChange={setCurrentConversation}
          userName={userName}
          onNewConversation={handleNewConversation}
        />
      </main>
      
      {/* Bottom Navigation */}
      <NexiBottomNav 
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />
      
      {/* Review Prompt */}
      <ReviewPrompt />
      
      {/* Image Generator Modal */}
      <AnimatePresence>
        {showImageGenerator && (
          <ImageGenerator 
            onClose={() => setShowImageGenerator(false)}
            onImageGenerated={(image) => {
              console.log("Generated image:", image.prompt);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Home;
